import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  pulseAnimation,
  segmentIndicatorAnimation,
  badgeAnimation,
  commandButtonAnimation,
  pillDeleteAnimation
} from './animation-variants';
import type { SlashCommand } from '../slash-commands/slash-commands-config';
import React from 'react';
import { EditorTooltip, EditorTooltipTrigger, EditorTooltipContent } from './editor-tooltip';
import { TooltipProvider } from '@/components/ui/tooltip';

export function PulseEffect() {
  return (
    <motion.div
      className="absolute inset-0 rounded-lg bg-blue-400/25"
      variants={pulseAnimation}
      initial="initial"
      animate="animate"
    />
  );
}

interface SegmentIndicatorProps {
  value: number;
  onComplete: () => void;
}

export function SegmentIndicator({ value, onComplete }: SegmentIndicatorProps) {
  return (
    <motion.div
      className={cn(
        'absolute -top-4 left-1/2 -translate-x-1/2 text-xs font-medium',
        value > 0 ? 'text-blue-500' : 'text-red-500'
      )}
      variants={segmentIndicatorAnimation}
      initial="initial"
      animate="animate"
      onAnimationComplete={onComplete}
    >
      {value > 0 ? '+1' : '-1'}
    </motion.div>
  );
}

interface AnimatedBadgeProps {
  children: React.ReactNode;
  variant?: 'mms' | 'sms';
}

export const AnimatedBadge = React.forwardRef<HTMLDivElement, AnimatedBadgeProps>(
  ({ children, variant = 'sms' }: AnimatedBadgeProps, ref) => {
    const isMMS = variant === 'mms';

    return (
      <motion.div
        ref={ref}
        className="relative"
        layout
        style={{ position: 'relative' }}
        variants={badgeAnimation}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        {isMMS && <PulseEffect />}
        <Badge
          variant="secondary"
          className={cn(
            'px-2 py-0.5 text-xs rounded-lg',
            isMMS
              ? 'bg-badge-mms-background text-badge-mms-foreground hover:bg-badge-mms-background/90'
              : 'bg-badge-sms-background text-badge-sms-foreground hover:bg-badge-sms-background/90'
          )}
        >
          {children}
        </Badge>
      </motion.div>
    );
  }
);

AnimatedBadge.displayName = 'AnimatedBadge';

interface AnimatedCountProps {
  value: number;
  total: number;
  className?: string;
}

export function AnimatedCount({ value, total, className }: AnimatedCountProps) {
  return (
    <div className={cn('inline-flex gap-[1px]', className)}>
      <motion.span
        key={value}
        initial={{ opacity: 0.6, y: -2 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
      >
        {value}
      </motion.span>
      <span>/</span>
      <span>{total}</span>
    </div>
  );
}

export const AnimatedCommandList = motion.div;

interface AnimatedCommandButtonProps {
  command: SlashCommand;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

export function AnimatedCommandButton({
  command,
  isSelected,
  onSelect,
  onHover
}: AnimatedCommandButtonProps) {
  const button = (
    <motion.button
      className={cn(
        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-left',
        isSelected ? 'bg-accent/50' : 'hover:bg-accent/30',
        command.disabled ? 'opacity-50 cursor-not-allowed' : ''
      )}
      onClick={() => {
        if (!command.disabled) {
          onSelect();
        }
      }}
      onMouseEnter={onHover}
      disabled={command.disabled}
      variants={commandButtonAnimation}
      initial="initial"
      whileHover="whileHover"
      whileTap="whileTap"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-background/80 border">
        {command.icon}
      </div>
      <div className="flex-1">
        <div className="font-medium leading-none mb-1">{command.label}</div>
        <div className="text-xs text-muted-foreground leading-none">{command.description}</div>
      </div>
    </motion.button>
  );

  if (command.disabled && command.disabledReason) {
    return (
      <TooltipProvider>
        <EditorTooltip>
          <EditorTooltipTrigger asChild>{button}</EditorTooltipTrigger>
          <EditorTooltipContent>
            <p>{command.disabledReason}</p>
          </EditorTooltipContent>
        </EditorTooltip>
      </TooltipProvider>
    );
  }

  return button;
}

interface AnimatedPillProps {
  children: React.ReactNode;
  isDeleting: boolean;
  className?: string;
}

export const AnimatedPill = React.forwardRef<HTMLDivElement, AnimatedPillProps>(
  ({ children, isDeleting, className }: AnimatedPillProps, ref) => {
    return (
      <motion.span
        ref={ref}
        className={cn('relative', className)}
        variants={pillDeleteAnimation}
        animate={isDeleting ? 'exit' : 'initial'}
      >
        {children}
      </motion.span>
    );
  }
);
AnimatedPill.displayName = 'AnimatedPill';
